import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { ChartOptions } from "chart.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
ChartJS.register(
  TimeScale,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);
import { BUCKETS, STATUS_CODE, openNotificationWithIcon } from "../../../utils";
import { NotAccessibleFallback } from "../../../components";
import moment from "moment";
import { Select } from "antd";

const { Option } = Select;

interface APICallTrendProps {
  data: {
    tokenData: {
      date: string;
      feedbackCount: {
        name: string;
        count: number;
      }[];
    }[];
  };
  range: string;
}



const processAPIData = (
  data: APICallTrendProps["data"],
  granularity: string
) => {
  const bucketKeys = Object.keys(BUCKETS)
  if (!data.tokenData || data.tokenData.length === 0) {
    const empty = bucketKeys.reduce((acc, bucket) => {
      acc[bucket] = [];
      return acc;
    }, {} as Record<string, { date: string; value: number }[]>);
    return empty;
  }

  const result: Record<string, Map<string, number>> = bucketKeys.reduce(
    (acc, bucket) => {
      acc[bucket] = new Map();
      return acc;
    },
    {} as Record<string, Map<string, number>>
  );

  const EXCLUDED_FEEDBACK_TYPES = [
    "EMPTY_PAGES",
    "INVALID_PLACEHOLDER",
    "NO_MATCHING_METAPAGES",
  ];

  data.tokenData.forEach(({ date, feedbackCount }) => {
    let formattedDate: string;
    if (granularity === "weeks") {
      formattedDate = moment(date).format("YYYY-[W]WW");
    } else if (granularity === "months") {
      formattedDate = moment(date).format("YYYY-MM");
      // } else if (granularity === "years") {
      //   formattedDate = moment(date).format("YYYY");
    } else {
      formattedDate = moment(date).format("YYYY-MM-DD");
    }

    let total = 0;
    feedbackCount.forEach(({ name, count }) => {
      // Skip excluded feedback types
      if (!EXCLUDED_FEEDBACK_TYPES.includes(name)) {
        if (bucketKeys.includes(name)) {
          result[name].set(
            formattedDate,
            (result[name].get(formattedDate) || 0) + count
          );
        }
        total += count;
      }
    });
    result["TOTAL"].set(
      formattedDate,
      (result["TOTAL"].get(formattedDate) || 0) + total
    );
  });

  const finalResult = bucketKeys.reduce((acc, bucket) => {
    acc[bucket] = Array.from(result[bucket], ([date, value]) => ({
      date,
      value,
    }));
    return acc;
  }, {} as Record<string, { date: string; value: number }[]>);

  return finalResult;
};

export const APICallTrend = ({ data, range }: APICallTrendProps) => {
  const [loading, setLoading] = useState(false);
  const [tokenUsageData, setTokenUsageData] = useState<Record<string, any[]>>(
    {}
  );
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [timeGranularity, setTimeGranularity] = useState("days");
  const [availableGranularities, setAvailableGranularities] = useState<
    string[]
  >(["days"]);

  const optionsByRange: Record<string, string[]> = {
    day: ["days", "weeks", "months"],
    week: ["weeks", "months"],
    month: ["months"],
    // year: ["years"],
  };

  useEffect(() => {
    const validGranularities = optionsByRange[range] || ["days"];
    setAvailableGranularities(validGranularities);
    if (!validGranularities.includes(timeGranularity)) {
      setTimeGranularity(validGranularities[0]);
    }
  }, [range, timeGranularity]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const processedData = processAPIData(data, timeGranularity);
        setTokenUsageData(processedData);
      } catch (err: any) {
        if (err.response && err.response.status === STATUS_CODE.FORBIDDEN) {
          setIsAuthorized(false);
        } else {
          openNotificationWithIcon(
            "",
            err.response?.data?.message || "Error fetching data",
            "error"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [data, timeGranularity]);

  const chartData = useMemo(() => {
    const labels = tokenUsageData?.TOTAL?.map((item) => item.date) || [];

    const colors = [
      "#DA7C7C",
      "#7280CD",
      "#5EADA6",
      "#E2B039",
      "#AE68C7",
      "#47B784",
      "#FC8338",
      "#7D5A50",
      "#4E6E81",
      "#A1C181",
    ];

    return {
      labels,
      datasets: Object.keys(BUCKETS).map((bucketKey, idx) => ({
        label: BUCKETS[bucketKey as keyof typeof BUCKETS],
        data: tokenUsageData[bucketKey]?.map((item) => item.value) || [],
        fill: false,
        borderColor: colors[idx % colors.length],
        pointBackgroundColor: colors[idx % colors.length],
        pointBorderColor: colors[idx % colors.length],
        tension: 0.4,
      })),
    };
  }, [tokenUsageData]);

  const options = useMemo(() => {
    return {
      scales: {
        x: {
          type: "category",
          stacked: false,
          grid: {
            drawOnChartArea: false,
          },
        },
        y: {
          stacked: false,
          beginAtZero: true,
        },
      },
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          position: "bottom",
        },
      },
    } as ChartOptions<"line">;
  }, []);

  const onTimeGranularityChange = (granularity: string) => {
    setTimeGranularity(granularity);
  };

  if (!isAuthorized) {
    return <NotAccessibleFallback />;
  }

  return (
    <div className="statistics">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h4 className="summary-title">Feedback Counts Trend</h4>
          <p>Total feedback bucket trends across selected time range</p>
        </div>
        <div style={{ paddingBottom: "20px" }}>
          <Select
            value={timeGranularity}
            style={{ width: 120, height: 40 }}
            onChange={onTimeGranularityChange}
          >
            {availableGranularities.map((granularity) => (
              <Option key={granularity} value={granularity}>
                {granularity.charAt(0).toUpperCase() + granularity.slice(1)}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="height">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};
